'use client';

import { type RefObject, type ReactNode, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { Curtain, type ICurtainProps } from './index';

/**
 * Options for the `useCurtain` hook.
 */
export interface IUseCurtainOptions {
  /**
   * Whether the curtain should be visible.
   */
  show: boolean;

  /**
   * Called when user indicates they want to close whatever is on top of the curtain.
   */
  onClose?: () => void;

  /**
   * Optional ref to an anchor element where the curtain portal should be rendered.
   * If not provided or if `anchorRef.current` is null, defaults to `document.body`.
   */
  anchorRef?: RefObject<HTMLElement | null>;

  /**
   * Optional class name to apply to the curtain.
   */
  className?: ICurtainProps['className'];

  /**
   * Optional inline styles to apply to the curtain.
   */
  style?: ICurtainProps['style'];
}

/**
 * Hook to control a `<Curtain />` component that can be portaled to any anchor element.
 *
 * This hook provides full control over curtain positioning, allowing the curtain to be
 * rendered independently from other elements. The curtain is portaled to the specified
 * anchor element (or `document.body` if none is provided).
 *
 * @example
 * ```tsx
 * const anchorRef = useRef<HTMLDivElement>(null);
 * const CurtainPortal = useCurtain({
 *   show: isModalOpen,
 *   onClose: handleClose,
 *   anchorRef
 * });
 *
 * return (
 *   <div ref={anchorRef}>
 *     {CurtainPortal}
 *     <Modal>...</Modal>
 *   </div>
 * );
 * ```
 *
 * @param options - Configuration options for the curtain.
 * @returns An object containing the curtain portal component and a unique ID.
 */
export function useCurtain({
  show,
  onClose,
  anchorRef,
  className,
  style
}: IUseCurtainOptions): ReactNode {
  /**
   * Memoized curtain portal component.
   * Only renders when `show` is true and we have a valid portal target.
   */
  const CurtainPortal = useMemo(() => {
    if (!show) {
      return null;
    }

    return createPortal(
      <Curtain
        onClose={onClose}
        className={className}
        style={style}
      />,
      anchorRef?.current ?? document.body
    );
  }, [show, onClose, className, style, anchorRef]);

  return CurtainPortal;
}
